import {
  createSignal,
  createEffect,
  createResource,
  onCleanup,
  onMount,
  Accessor,
} from "solid-js";
import { makeUPIAppRequest } from "~/data/products";
import { getPaymentOrder } from "~/data/products";
import { clientRepo } from "~/server/apis/client_repo";
import { PaymentOrderStatus } from "~/server/types/order";
import { rewriteURL, upiApps, UPIApp } from "~/data/upi_apps";
import { isMobile } from "~/utils/platform";
import { showSnackBar } from "~/shared_states/snackbar";

export interface UsePaymentLoaderProps {
  orderId: string;
  amount: number;
  onClose: () => void;
  reInit: () => void;
  onPaymentComplete: () => void;
  onPaymentFailed: (failureReason: string) => void;
}

export interface UsePaymentLoaderReturn {
  // Props
  amount: number;
  orderId: string;

  // Order state
  orderStatus: Accessor<PaymentOrderStatus>;
  isExpired: Accessor<boolean>;

  // QR code state (desktop)
  qrLink: Accessor<string>;
  timeLeft: Accessor<number>;
  minutes: Accessor<number>;
  seconds: Accessor<number>;
  isQrLoading: Accessor<boolean>;

  // UPI app launch state
  showProcessingLoader: Accessor<boolean>;
  loadingAppName: Accessor<string | null>;

  // Actions
  launchUpiApp: (app: UPIApp) => Promise<void>;
  launchDefaultUpiIntent: () => Promise<void>;
  handleTryAgain: () => void;

  // UPI apps list
  upiAppsList: typeof upiApps;
}

export function usePaymentLoader(
  props: UsePaymentLoaderProps
): UsePaymentLoaderReturn {
  // Order status management
  const [orderStatus, setOrderStatus] =
    createSignal<PaymentOrderStatus>("ACTIVE");

  // QR code state
  const [qrLink, setQrLink] = createSignal<string>("");
  const [timeLeft, setTimeLeft] = createSignal<number>(-1);
  const [isQrLoading, setIsQrLoading] = createSignal(true);

  // Processing state
  const [showProcessingLoader, setShowProcessingLoader] = createSignal(false);
  const [loadingAppName, setLoadingAppName] = createSignal<string | null>(null);

  // Derived time values
  const [minutes, setMinutes] = createSignal(0);
  const [seconds, setSeconds] = createSignal(0);

  // Check if QR expired
  const isExpired = () => timeLeft() === 0;

  // Timer reference
  let timer: ReturnType<typeof setInterval> | undefined;

  // Polling for order status
  const [order, manageOrder] = createResource(async () => {
    const orderData = await getPaymentOrder(props.orderId);
    if (orderStatus() !== orderData.status) {
      setOrderStatus(orderData.status);
    }
    return orderData;
  });

  // Poll order status every 5 seconds
  const pollInterval = setInterval(() => {
    if (!order.loading) {
      manageOrder.refetch();
    }
  }, 5000);

  // Initialize QR code for desktop
  const initializeQrCode = async () => {
    try {
      setIsQrLoading(true);
      const orderResponse = await makeUPIAppRequest(props.orderId, "PAYTM");
      setQrLink(orderResponse.link);
      setIsQrLoading(false);
    } catch (error: any) {
      showSnackBar({
        level: "error",
        message: error.message ?? "Something went wrong",
      });
      setIsQrLoading(false);
    }
  };

  // Get order expiry details
  const getOrderDetail = async (orderId: string) => {
    try {
      const expiresAt = (await clientRepo.getPaymentOrderDetails(orderId))
        .expiresAt;
      if (expiresAt === null) return;

      const expiresAtLocalTime =
        new Date(expiresAt).getTime() +
        Math.abs(new Date(expiresAt).getTimezoneOffset() * 60 * 1000);
      const timeLeftToExpire = Math.floor(
        (expiresAtLocalTime - new Date().getTime()) / 1000
      );
      setTimeLeft(timeLeftToExpire);

      timer = setInterval(() => {
        if (timeLeft() <= 0) {
          setTimeLeft(0);
          clearInterval(timer);
          return;
        }
        setTimeLeft(timeLeft() - 1);
      }, 1000);
    } catch (error: any) {
      showSnackBar({
        level: "error",
        message: error.message ?? "Failed to get order details",
      });
    }
  };

  // Update minutes and seconds when timeLeft changes
  createEffect(() => {
    setMinutes(Math.floor(timeLeft() / 60));
    setSeconds(timeLeft() % 60);
  });

  // Handle order status changes
  createEffect(() => {
    if (!order()) return;
    if (order.loading) return;
    if (order.error) return;

    if (order()?.status !== "ACTIVE") {
      clearInterval(pollInterval);
      if (order()!.status === "PAID") {
        props.onPaymentComplete();
      }
      if (order()!.status === "FAILED") {
        props.onPaymentFailed(order()!.failureReason ?? "Payment failed");
      }
    }
  });

  // Initialize on mount (desktop only)
  onMount(async () => {
    if (!isMobile()) {
      await initializeQrCode();
      await getOrderDetail(props.orderId);
    }
  });

  // Cleanup
  onCleanup(() => {
    clearInterval(pollInterval);
    if (timer) clearInterval(timer);
  });

  // Launch specific UPI app (iOS)
  const launchUpiApp = async (app: UPIApp) => {
    try {
      setLoadingAppName(app.name);
      setShowProcessingLoader(true);

      const orderResponse = await makeUPIAppRequest(
        props.orderId,
        app.backendName
      );
      const url = rewriteURL(orderResponse.link, app);

      const loc = window.top?.location;
      if (loc) {
        loc.href = url;
      } else {
        window.location.href = url;
      }
    } catch (error: any) {
      showSnackBar({
        level: "error",
        message: error.message ?? "Something went wrong",
      });
      setShowProcessingLoader(false);
      setLoadingAppName(null);
    }
  };

  // Launch default UPI intent (Android)
  const launchDefaultUpiIntent = async () => {
    try {
      setShowProcessingLoader(true);
      const orderResponse = await makeUPIAppRequest(props.orderId, "PAYTM");

      const loc = window.top?.location;
      if (loc) {
        loc.href = orderResponse.link;
      } else {
        window.location.href = orderResponse.link;
      }
    } catch (error: any) {
      showSnackBar({
        level: "error",
        message: error.message ?? "Something went wrong",
      });
      setShowProcessingLoader(false);
    }
  };

  // Handle try again action
  const handleTryAgain = () => {
    props.onClose();
    // Pop animation takes 250ms
    setTimeout(() => {
      props.reInit();
    }, 300);
  };

  return {
    amount: props.amount,
    orderId: props.orderId,
    orderStatus,
    isExpired,
    qrLink,
    timeLeft,
    minutes,
    seconds,
    isQrLoading,
    showProcessingLoader,
    loadingAppName,
    launchUpiApp,
    launchDefaultUpiIntent,
    handleTryAgain,
    upiAppsList: upiApps,
  };
}
